import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { KeyValue } from "@/main/models/common";
import { SubModelRequest, VehicleFiltersRequest, VehicleYearRequest } from "@/main/models/request";
import { getTranslateByKey, isNullUndefined, removeEmptyValues } from "@/main/services/utils";
import { VehicleAutoCompleteResponse } from "@/main/models/response";
import { VehicleAutoCompleteTypeEnum } from "@/main/services/enum/VehicleAutoCompleteTypeEnum";

import KogAutoCompleteControlled from "../inputAutoComplete/autoCompleteControlled";
import KogAutoCompleteVehicleControlled from "../inputAutoComplete/autoCompleteVehicleControlled";
import VehicleAutoCompleteComponent from "../vehicle/VehicleAutoCompleteComponent";
import useVehicleFilterOptions from "../vehicleFilterFields/useVehicleFilterOptions";
import * as S from "./styles";

export interface IVehicleCascadeFieldNames {
	country: string;
	manufacturer: string;
	model: string;
	trim: string;
	year: string;
}

export const VEHICLE_CASCADE_DEFAULT_FIELD_NAMES: IVehicleCascadeFieldNames = {
	country: "vehicleCountry",
	manufacturer: "manufacturer",
	model: "model",
	trim: "trim",
	year: "year",
};

interface IVehicleCascadeFilterFieldsProps {
	idPrefix: string;
	fieldNames?: Partial<IVehicleCascadeFieldNames>;
	isRequired?: boolean;
	isDisabled?: boolean;
	isDisabledCountry?: boolean;
	showVehicleSearch?: boolean;
	guidCountryToSelect?: string | null;
	onVehicleChange?: () => void;
}

function VehicleCascadeFilterFields({
	idPrefix,
	fieldNames,
	isRequired = false,
	isDisabled = false,
	isDisabledCountry = false,
	showVehicleSearch = false,
	guidCountryToSelect,
	onVehicleChange,
}: IVehicleCascadeFilterFieldsProps) {
	const names = { ...VEHICLE_CASCADE_DEFAULT_FIELD_NAMES, ...fieldNames };
	const {
		control,
		setValue,
		getValues,
		formState: { errors },
	} = useFormContext();
	const {
		countries,
		manufacturers,
		models,
		subModels,
		years,
		isLoadingCountry,
		isLoadingManufacturer,
		isLoadingModel,
		isLoadingSubModel,
		isLoadingYear,
		loadCountries,
		loadManufacturers,
		loadModels,
		loadSubModels,
		loadYears,
		clearManufacturers,
		clearModels,
		clearSubModels,
		clearYears,
	} = useVehicleFilterOptions();

	const watcherCountry = useWatch({
		control,
		name: names.country,
	}) as KeyValue | null;
	const watcherManufacturer = useWatch({
		control,
		name: names.manufacturer,
	}) as KeyValue | null;
	const watcherModel = useWatch({
		control,
		name: names.model,
	}) as KeyValue | null;
	const watcherTrim = useWatch({
		control,
		name: names.trim,
	}) as KeyValue | null;

	function getSelectedKey(option: KeyValue | null) {
		return isNullUndefined(option) ? undefined : String(option.key);
	}

	function generateOption(key?: string | null, value?: string | null) {
		if (isNullUndefined(key) || isNullUndefined(value) || key === "") return null;

		return { key, value };
	}

	function hasRequiredError(fieldName: string) {
		return errors[fieldName]?.type === "required";
	}

	function getRequiredMessage(fieldName: string, translateKey: string) {
		return hasRequiredError(fieldName) ? getTranslateByKey(translateKey, true) : "";
	}

	const hasSingleCountry = !isNullUndefined(countries) && countries.length === 1;
	const guidCountry = getSelectedKey(watcherCountry);
	const guidManufacturer = getSelectedKey(watcherManufacturer);
	const guidModel = getSelectedKey(watcherModel);
	const guidTrim = getSelectedKey(watcherTrim);

	const isCountryFieldDisabled = isDisabled || isDisabledCountry || hasSingleCountry || isLoadingCountry;
	const isManufacturerFieldDisabled = isDisabled || isLoadingManufacturer || isNullUndefined(guidCountry);
	const isModelFieldDisabled = isDisabled || isLoadingModel || isNullUndefined(guidManufacturer);
	const isTrimFieldDisabled = isDisabled || isLoadingSubModel || isNullUndefined(guidModel);
	const isYearFieldDisabled = isDisabled || isLoadingYear || isNullUndefined(guidTrim);

	function clearFromTrim() {
		setValue(names.year, null);
	}

	function clearFromModel() {
		setValue(names.trim, null);
		clearFromTrim();
	}

	function clearFromManufacturer() {
		setValue(names.model, null);
		clearFromModel();
	}

	function clearFromCountry() {
		setValue(names.manufacturer, null);
		clearFromManufacturer();
	}

	function handleVehicleSearchChange(vehicle: VehicleAutoCompleteResponse | null) {
		if (isNullUndefined(vehicle)) return;

		setValue(names.country, generateOption(vehicle.guidVehicleCountry, vehicle.nameVehicleCountry));
		setValue(names.year, generateOption(vehicle.guidYear, vehicle.year));
		setValue(names.manufacturer, generateOption(vehicle.guidVehicleManufacturer, vehicle.manufacturer));
		setValue(names.model, generateOption(vehicle.guidVehicleModel, vehicle.model));
		setValue(names.trim, generateOption(vehicle.guidSubModel, vehicle.subModel));

		if (onVehicleChange) onVehicleChange();
	}

	useEffect(() => {
		loadCountries();
	}, []);

	useEffect(() => {
		const hasCountrySelected = !isNullUndefined(getValues(names.country));
		if (hasCountrySelected || isNullUndefined(countries)) return;

		const optionFromGuid = isNullUndefined(guidCountryToSelect)
			? null
			: countries.find((option) => String(option.key) === String(guidCountryToSelect));
		const singleCountryOption = hasSingleCountry ? countries[0] : null;
		const optionToSelect = optionFromGuid ?? singleCountryOption;
		if (isNullUndefined(optionToSelect)) return;

		setValue(names.country, optionToSelect);
	}, [countries, guidCountryToSelect]);

	useEffect(() => {
		if (isNullUndefined(guidCountry)) {
			clearManufacturers();
			return;
		}

		const filter: VehicleFiltersRequest = removeEmptyValues({
			guidCountry,
		});

		loadManufacturers(filter);
	}, [guidCountry]);

	useEffect(() => {
		if (isNullUndefined(guidManufacturer)) {
			clearModels();
			return;
		}

		const filter: VehicleFiltersRequest = removeEmptyValues({
			guidVehicleManufacturer: guidManufacturer,
			guidCountry,
		});

		loadModels(filter);
	}, [guidCountry, guidManufacturer]);

	useEffect(() => {
		if (isNullUndefined(guidModel)) {
			clearSubModels();
			return;
		}

		const filter: SubModelRequest = removeEmptyValues({
			guidManufacturer,
			guidModel,
			guidCountry,
		});

		loadSubModels(filter);
	}, [guidCountry, guidManufacturer, guidModel]);

	useEffect(() => {
		if (isNullUndefined(guidManufacturer) || isNullUndefined(guidModel) || isNullUndefined(guidTrim)) {
			clearYears();
			return;
		}

		const filter: VehicleYearRequest = {
			guidManufacturer,
			guidModel,
			guidSubModel: guidTrim,
			guidCountry: guidCountry,
		};

		loadYears(filter);
	}, [guidCountry, guidManufacturer, guidModel, guidTrim]);

	return (
		<>
			{showVehicleSearch && (
				<S.ContainerVehicleSearch>
					<VehicleAutoCompleteComponent
						handleChangeVehicle={handleVehicleSearchChange}
						vehicleAutoCompleteType={VehicleAutoCompleteTypeEnum.Full}
						hasBorderStyle
						label={getTranslateByKey("information.vehicle")}
						disabled={isDisabled}
					/>
				</S.ContainerVehicleSearch>
			)}
			<KogAutoCompleteControlled
				id={`${idPrefix}-autocomplete-country`}
				nameCustom={names.country}
				hasErrorCustom={hasRequiredError(names.country)}
				errorMessageCustom={getRequiredMessage(names.country, "information.countryRequired")}
				label={getTranslateByKey("information.countryVehicle")}
				control={control}
				rulesCustom={{ required: isRequired }}
				options={countries}
				isLoading={isLoadingCountry}
				onChangeCustom={() => {
					clearFromCountry();
					if (onVehicleChange) onVehicleChange();
				}}
				setValueCustom={setValue}
				disabled={isCountryFieldDisabled}
			/>
			<KogAutoCompleteVehicleControlled
				id={`${idPrefix}-autocomplete-manufacturer`}
				nameCustom={names.manufacturer}
				hasErrorCustom={hasRequiredError(names.manufacturer)}
				errorMessageCustom={getRequiredMessage(names.manufacturer, "information.manufacturerRequired")}
				label={getTranslateByKey("information.manufacturer")}
				control={control}
				rulesCustom={{ required: isRequired }}
				options={manufacturers}
				isLoading={isLoadingManufacturer}
				onChangeCustom={() => {
					clearFromManufacturer();
					if (onVehicleChange) onVehicleChange();
				}}
				setValueCustom={setValue}
				disabled={isManufacturerFieldDisabled}
			/>
			<KogAutoCompleteVehicleControlled
				id={`${idPrefix}-autocomplete-model`}
				nameCustom={names.model}
				hasErrorCustom={hasRequiredError(names.model)}
				errorMessageCustom={getRequiredMessage(names.model, "information.modelRequired")}
				label={getTranslateByKey("information.model")}
				control={control}
				rulesCustom={{ required: isRequired }}
				options={models}
				isLoading={isLoadingModel}
				onChangeCustom={() => {
					clearFromModel();
					if (onVehicleChange) onVehicleChange();
				}}
				setValueCustom={setValue}
				disabled={isModelFieldDisabled}
			/>
			<KogAutoCompleteVehicleControlled
				id={`${idPrefix}-autocomplete-trim`}
				nameCustom={names.trim}
				hasErrorCustom={hasRequiredError(names.trim)}
				errorMessageCustom={getRequiredMessage(names.trim, "information.trimRequired")}
				label={getTranslateByKey("information.trim")}
				control={control}
				rulesCustom={{ required: isRequired }}
				options={subModels}
				isLoading={isLoadingSubModel}
				onChangeCustom={() => {
					clearFromTrim();
					if (onVehicleChange) onVehicleChange();
				}}
				setValueCustom={setValue}
				disabled={isTrimFieldDisabled}
			/>
			<KogAutoCompleteControlled
				id={`${idPrefix}-autocomplete-year`}
				nameCustom={names.year}
				hasErrorCustom={hasRequiredError(names.year)}
				errorMessageCustom={getRequiredMessage(names.year, "information.yearRequired")}
				label={getTranslateByKey("information.year")}
				control={control}
				rulesCustom={{ required: isRequired }}
				options={years}
				isLoading={isLoadingYear}
				onChangeCustom={() => {
					if (onVehicleChange) onVehicleChange();
				}}
				setValueCustom={setValue}
				disabled={isYearFieldDisabled}
			/>
		</>
	);
}

export default VehicleCascadeFilterFields;
